import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const PrefsContext = createContext(null);

const THEME_KEY = "traina.theme";
const PRIVACY_KEY = "traina.privacy";

// theme preference can be: "light" | "dark" | "auto"
function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark" || v === "auto") return v;
  } catch (_e) { /* ignore */ }
  return "dark";
}

function systemPrefersDark() {
  return typeof window !== "undefined"
    && window.matchMedia
    && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolveTheme(pref) {
  if (pref === "auto") return systemPrefersDark() ? "dark" : "light";
  return pref;
}

function applyTheme(theme) {
  document.documentElement.dataset.theme = theme;
}
function applyPrivacy(on) {
  document.documentElement.dataset.privacy = on ? "on" : "off";
}

export function PrefsProvider({ children }) {
  const [themePref, setThemePrefState] = useState(readStoredTheme);
  const [privacy, setPrivacyState] = useState(() => {
    try {
      return localStorage.getItem(PRIVACY_KEY) === "on";
    } catch (_e) {
      return false;
    }
  });

  // Apply resolved theme whenever preference changes
  useEffect(() => {
    applyTheme(resolveTheme(themePref));
  }, [themePref]);

  // When in "auto" mode, follow OS changes live
  useEffect(() => {
    if (themePref !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const fn = () => applyTheme(systemPrefersDark() ? "dark" : "light");
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, [themePref]);

  useEffect(() => {
    applyPrivacy(privacy);
  }, [privacy]);

  const setTheme = useCallback((next) => {
    setThemePrefState(next);
    try { localStorage.setItem(THEME_KEY, next); } catch (_e) { /* ignore */ }
  }, []);

  // Cycle: dark → light → auto → dark
  const cycleTheme = useCallback(() => {
    setThemePrefState((t) => {
      const next = t === "dark" ? "light" : t === "light" ? "auto" : "dark";
      try { localStorage.setItem(THEME_KEY, next); } catch (_e) { /* ignore */ }
      return next;
    });
  }, []);

  const togglePrivacy = useCallback(() => {
    setPrivacyState((p) => {
      const next = !p;
      try { localStorage.setItem(PRIVACY_KEY, next ? "on" : "off"); } catch (_e) { /* ignore */ }
      return next;
    });
  }, []);

  // Keyboard shortcut: ⌘/Ctrl + Shift + P → toggle privacy mode
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "P" || e.key === "p")) {
        // Don't fire when typing in an input/textarea/contenteditable
        const t = e.target;
        const tag = (t?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
        e.preventDefault();
        togglePrivacy();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePrivacy]);

  const value = useMemo(() => ({
    themePref,                              // "light" | "dark" | "auto"
    theme: resolveTheme(themePref),         // resolved: "light" | "dark"
    privacy,
    setTheme,
    toggleTheme: cycleTheme,
    cycleTheme,
    togglePrivacy,
  }), [themePref, privacy, setTheme, cycleTheme, togglePrivacy]);

  return (
    <PrefsContext.Provider value={value}>
      {children}
    </PrefsContext.Provider>
  );
}

export function usePrefs() {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error("usePrefs outside provider");
  return ctx;
}
